/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as $ from "jquery";
import {UiComponentConfig} from "../../generated/UiComponentConfig";
import {UiFieldEditingMode} from "../../generated/UiFieldEditingMode";
import {UiField} from "./UiField";
import {UiComponent} from "../UiComponent";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {UiComponentFieldConfig, UiComponentFieldSubCommandHandler, UiComponentFieldSubEventSource} from "../../generated/UiComponentFieldConfig";
import {TeamAppsUiComponentRegistry} from "../TeamAppsUiComponentRegistry";
import {createUiBorderCssObject, createUiColorCssString} from "../util/CssFormatUtil";
import {UiColorConfig} from "../../generated/UiColorConfig";
import {UiBorderConfig} from "../../generated/UiBorderConfig";

/**
 * @author Yann Massard (yamass@gmail.com)
 */

export class UiComponentField extends UiField<UiComponentFieldConfig, void> implements UiComponentFieldSubEventSource, UiComponentFieldSubCommandHandler {

	private component: UiComponent<UiComponentConfig>;
	private $fieldWrapper: JQuery;

	protected initialize(config: UiComponentFieldConfig, targetElement: Element, context: TeamAppsUiContext) {
		this.$fieldWrapper = $('<div class="UiComponentField"/>');
		this.setBackgroundColor(config.backgroundColor);
		this.setBorder(config.border);
		this.setSize(config.width, config.height);
		this.setComponent(config.component);
		this.$fieldWrapper.appendTo(targetElement);
	}

	isValidData(v: void): boolean {
		return true;
	}

	setBackgroundColor(backgroundColor: UiColorConfig): void {
		this.$fieldWrapper.css({
			"background-color": backgroundColor ? createUiColorCssString(backgroundColor) : ''
		});
	}

	setBorder(border: UiBorderConfig): void {
		this.$fieldWrapper.css(createUiBorderCssObject(border));
	}

	setComponent(component: UiComponentConfig): void {
		if (this.component != null) {
			this.component.destroy();
			this.component.getMainDomElement().remove();
		}
		this.component = this._context.createComponent(component);
		this.component.getMainDomElement().appendTo(this.$fieldWrapper);
	}

	setSize(width: number, height: number): void {
		this.$fieldWrapper.css({
			height: height ? `${height}px` : '',
			width: width ? `${width}px` : ''
		});
	}

	protected onAttachedToDom(): void {
		this.component.attachedToDom = true;
	}

	public getMainDomElement(): JQuery {
		return this.$fieldWrapper;
	}

	public getFocusableElement(): JQuery {
		return null;
	}

	protected displayCommittedValue(): void {
		// do nothing
	}

	focus(): void {
		// do nothing
	}

	destroy(): void {
		this.component.destroy();
		this.$fieldWrapper.remove();
	}

	getTransientValue(): void {
	}

	protected onEditingModeChanged(editingMode: UiFieldEditingMode): void {
		// do nothing (default implementation)
	}

	getDefaultValue(): void {
	}

	public valuesChanged(v1: void, v2: void): boolean {
		return false;
	}

	onResize(): void {
		this.component && this.component.reLayout(true);
	}
}

TeamAppsUiComponentRegistry.registerFieldClass("UiComponentField", UiComponentField);